/**
 * THE CAREER SCREEN — the RECORD of a career.
 *
 * Look at what a season did, save it, load it, come back to it. Four views over
 * the same played season — the standings, the money, the garage, the winter —
 * and a driver dossier hanging off every name in all of them.
 *
 * ===========================================================================
 * THIS SCREEN NO LONGER OWNS THE CAREER, AND THAT WAS THE BUG
 * ===========================================================================
 *
 * `current` and `history` used to be `useState` in this file. Nothing else in
 * the application could reach them, so Save had nothing to save, the record book
 * had nothing to print, and a league loaded from the cloud had nowhere to go.
 * They are PROPS now — `App.tsx` holds the one career every screen reads, and
 * the season home settles seasons into it.
 *
 * D-EZ-103 — NO SEASON STARTS HERE ANY MORE. The "Simulate a whole season"
 * button and its seed field were a second way to begin a league (no name, no
 * settings). Seasons start on Home and are simulated from the season home; this
 * screen reads them. Its first view is "Standings" (it was "The book", one of
 * three things called a book).
 *
 * WHERE THE NUMBERS COME FROM. All of them are read off the season fold and the
 * money ledger at the moment they are drawn. Nothing on this screen is stored,
 * cached, or recomputed by the component, which is the same rule the engine and
 * the database both keep. If a figure here is wrong, something upstream is
 * wrong, and that is the only arrangement under which a standings table is
 * worth looking at.
 *
 * SAVING LIVES IN ONE PLACE (D-EZ-99): Save league, in the Account area. This
 * screen no longer has a save of its own.
 */

import { useEffect, useMemo, useState } from 'react';
import type { CareerSeason } from '../lib/careerRunner.ts';
import { carBooks, privateers, shopBooks } from './garageBooks.ts';
import { formatDollars } from '../domain/finance/money.ts';
import { careerWinningsTable } from '../domain/finance/money.ts';
import DriverDossier from './DriverDossier.tsx';
import StatsBoard from './StatsBoard.tsx';
import { collectSources, type LiveSeasonLike, type StatsSource } from './statsModel.ts';
import { liveHistorySeason, nameResolver } from '../domain/history/index.ts';
import SeasonStory from './SeasonStory.tsx';
import { fromSeasonEvents } from '../domain/story.ts';
import { ordinal } from './boardModel.ts';
import { SEASON_RACES } from '../domain/schedule.ts';
import { garageChange, garageLine } from './garageModel.ts';
import { Cite } from './Cite.tsx';
import { useCiteText } from '../lib/prefs.ts';
import { leagueSeasons } from '../domain/story/league.ts';
import ChampionsMoment from './story/ChampionsMoment.tsx';
import HallOfFame from './story/HallOfFame.tsx';
import RivalryBoard from './story/RivalryBoard.tsx';
import Newsstand, { usePaperReader } from './story/Newsstand.tsx';
import WinterChapters from './chapters/WinterChapters.tsx';
import LeagueTimeline from './chapters/LeagueTimeline.tsx';
import { seasonArc, type ChapterSeason } from '../domain/chapters/index.ts';

const VIEW_LABEL: Record<View, string> = {
  review: 'The trophy',
  stats: 'Standings',
  money: 'The money',
  garage: 'The garage',
  winter: 'The winter',
  story: 'The season',
  news: 'Newsstand',
  hall: 'Hall of Fame',
  rivals: 'Rivalries',
  league: 'League history',
};

/**
 * D-EZ-102 adds three: 'review' (the trophy and the year in review — shown
 * first, once, when a season settles), 'news' (the newsstand: every edition of
 * the paper) and 'hall' (the Hall of Fame and the retired list).
 */
type View = 'stats' | 'money' | 'garage' | 'winter' | 'story' | 'review' | 'news' | 'hall' | 'rivals' | 'league';

/**
 * D-EZ-102 — HAS THIS BROWSER SHOWN THE TROPHY FOR THIS SEASON YET?
 *
 * A season that has just settled opens on the champion's moment, once. Kept per
 * browser (presentation memory only, never league data); a store that throws
 * just means the trophy shows again, which is harmless.
 */
const TROPHY_SEEN_KEY = 'rwbr.trophy.seen';
function trophySeen(key: string): boolean {
  try {
    const raw = window.localStorage.getItem(TROPHY_SEEN_KEY);
    return raw ? (JSON.parse(raw) as string[]).includes(key) : false;
  } catch {
    return false;
  }
}
function markTrophySeen(key: string): void {
  try {
    const raw = window.localStorage.getItem(TROPHY_SEEN_KEY);
    const list = raw ? (JSON.parse(raw) as string[]) : [];
    if (!list.includes(key)) window.localStorage.setItem(TROPHY_SEEN_KEY, JSON.stringify([...list.slice(-49), key]));
  } catch {
    /* presentation memory only */
  }
}

export interface CareerScreenProps {
  /** The season on the workbench. Owned by the shell; null when there is none. */
  career: CareerSeason | null;
  /** Earlier seasons of the same league, oldest first. */
  history: CareerSeason[];
  /** The league's name. Named when the league is created (D-EZ-103). */
  leagueName: string;
  /** A season being played race by race, when there is one. Read-only here. */
  seasonInPlay?: { seasonNumber: number; state: { nextRace: number } } | null;
  /**
   * THE LIVE SEASON ITSELF — the whole `SeasonState`, not just where it has got
   * to.
   *
   * This is the prop that turned this screen from a record of finished seasons
   * into a running one. A `SeasonState` already carries the standings refolded
   * after every race and the weekend's money settled as it happens; without it
   * here, the only thing this page could say about a season in progress was
   * that it was in progress.
   */
  live?: LiveSeasonLike | null;
  /** Go Home, where the season is played (race by race or simulated). */
  onPlayRaceByRace?: () => void;
  /** Go Home to the next season, between seasons. */
  onPlayNextSeason?: () => void;
}

export default function CareerScreen({
  career,
  history,
  leagueName,
  seasonInPlay = null,
  live = null,
  onPlayRaceByRace,
  onPlayNextSeason,
}: CareerScreenProps) {
  /**
   * The career on the workbench, under the name this file has always used for
   * it. It is a PROP now (see the header): one alias line, and every reference
   * below still reads `current`, so lifting the state did not rewrite the views.
   */
  const current = career;
  const [view, setView] = useState<View>('stats');
  const [dossier, setDossier] = useState<string | null>(null);

  /*
    D-EZ-102 — THE TROPHY FIRST. A settled season this browser has not yet
    presented opens on the champion's moment, before the winter's numbers.
  */
  useEffect(() => {
    if (!current) return;
    const key = `${current.season.seasonId}|${current.seasonNumber}`;
    if (trophySeen(key)) return;
    markTrophySeen(key);
    setView('review');
  }, [current]);

  /*
    D-EZ-99 — ONE SAVE. The Career tab used to have its own "Save this season"
    (database rows, readable by anyone, listed under "Already saved" even when
    signed out). There is now one Save league, in the Account area, and it keeps
    every season; this screen no longer saves or lists other people's leagues.
  */

  /**
   * EVERY SEASON THIS SHELL CAN REACH, through one door.
   *
   * The settled ones and the one being raced right now, oldest first, with the
   * live season dropped the moment a settled `CareerSeason` exists for the same
   * year (`collectSources`). This is what the picker lists and what the all-time
   * table is folded from.
   */
  const sources: StatsSource[] = useMemo(
    () => collectSources({ history, career: current, live }),
    [history, current, live],
  );

  /**
   * THE DOSSIER'S SOURCE WHILE A SEASON IS BEING RACED — D-EZ-49.
   *
   * Howard, 18 Aug 2026: *"In The Book, if I click on a racer name it ought to
   * open their driver card modal like the other locations."*
   *
   * It opened onto nothing, because `DriverDossier` finds a driver's card
   * through the `CareerSeason` and there is no `CareerSeason` until the winter
   * lands. This is the live season handed over in the shape that component now
   * takes: the cards, the garage, the fold as it stands, and the championship
   * order so ‹ and › walk down the standings.
   */
  const seasonSource = useMemo(() => {
    if (!live) return undefined;
    const folds = live.state.standingsAfterRace;
    const standings = folds[folds.length - 1];
    return {
      seasonNumber: live.seasonNumber,
      // `state.roster` and not the one the season STARTED with: a rookie signed
      // in the winter is in it and a dead man is not, which is the difference
      // between a working card and an empty modal for anybody who joined late.
      cards: new Map(live.state.roster.map((c) => [c.driverId, c])),
      teams: live.teams,
      standings,
      throughRace: folds.length,
      totalRaces: live.state.schedule.races.length,
      order: standings?.drivers.map((d) => d.driverId) ?? [],
    };
  }, [live]);

  /*
    D-EZ-103 — ONE SEASON START. This tab used to simulate whole seasons of its
    own ("Simulate a whole season", "Start over", "Next season → (simulated)")
    with its own seed field — a second way to begin a league that skipped the
    league's name and settings. Seasons now start on Home (league creation, or
    "Season N+1" between seasons) and are simulated from the season home; this
    tab is where the league is READ.
  */
  return (
    <section className="career" aria-label="Career">
      {/* D-EZ-103 — between seasons, the way on is on Home. */}
      {current && onPlayNextSeason && !seasonInPlay && (
        <div className="career__next">
          <button type="button" className="btn btn--hot career__nextbtn" onClick={onPlayNextSeason}>
            Season {current.seasonNumber + 1} →
          </button>
        </div>
      )}

      {/*
        THE EMPTY STATE — honest, and two clicks wide.

        A season played race by race lands here the moment its winter is settled,
        so the interesting case is "one is under way": say where it is and offer
        the way back to it, rather than explaining the two paths at length.
      */}
      {sources.length === 0 && (
        <div className="career__empty">
          <h2>No season on the workbench</h2>
          {seasonInPlay ? (
            <p>
              Season {seasonInPlay.seasonNumber} is being played race by race —{' '}
              {seasonInPlay.state.nextRace - 1} of {SEASON_RACES} run. It lands here when the year is
              settled.
            </p>
          ) : (
            <p>Start a league on Home and its seasons are read here.</p>
          )}
          {onPlayRaceByRace && (
            <button type="button" className="btn btn--hot" onClick={onPlayRaceByRace}>
              {seasonInPlay ? 'Back to the season' : 'Go to Home'}
            </button>
          )}
        </div>
      )}

      {sources.length > 0 && (
        <SeasonBody
          career={current}
          history={history}
          sources={sources}
          leagueName={leagueName}
          view={view}
          setView={setView}
          onDriver={setDossier}
          onPlayRaceByRace={onPlayRaceByRace}
          liveThrough={
            live ? { seasonNumber: live.seasonNumber, racesRun: live.state.nextRace - 1 } : null
          }
          live={live}
        />
      )}

      {dossier && (
        <DriverDossier
          driverId={dossier}
          career={current ?? undefined}
          season={seasonSource}
          history={history}
          /* D-EZ-60 — so the career total counts the year being raced. */
          liveSeason={live}
          onClose={() => setDossier(null)}
          onSelectDriver={setDossier}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

/**
 * THE PAGE UNDER THE CONTROLS — and the one change that matters most on it.
 *
 * `career` is NULLABLE now. It used to be the reason this whole block existed:
 * no settled season, nothing to draw. But a season being raced has standings
 * after every race, money settled every weekend and a driver's whole week-by-week
 * record — all of it in `sources` — so the stat board draws with or without a
 * winter. The four OTHER views still need a settled season, because the money
 * report, the crew grades, the re-rates and the season story are things the
 * winter produces; those tabs simply are not offered until there is one.
 */
function SeasonBody({
  career,
  history,
  sources,
  leagueName,
  view,
  setView,
  onDriver,
  onPlayRaceByRace,
  liveThrough,
  live = null,
}: {
  career: CareerSeason | null;
  history: CareerSeason[];
  live?: LiveSeasonLike | null;
  sources: StatsSource[];
  leagueName: string;
  view: View;
  setView: (v: View) => void;
  onDriver: (id: string) => void;
  onPlayRaceByRace?: () => void;
  liveThrough: { seasonNumber: number; racesRun: number } | null;
}) {
  const champion = career?.season.championId ?? null;
  // Every season of the league, not just this one — a driver who retired two
  // winters ago still has a name (D-EZ-95; audit: retired drivers printed as ids).
  const nameOf = useMemo(
    () =>
      nameResolver([
        ...history,
        ...(career ? [career] : []),
        ...(live ? [liveHistorySeason(live)] : []),
      ]),
    [career, history, live],
  );

  /**
   * Career winnings across every season played this sitting. Folded from the
   * money streams, not accumulated — the same call the database's
   * `v_career_winnings` view makes, and it returns the same numbers.
   */
  const winnings = useMemo(() => {
    if (!career) return [];
    const all = [...history, career].flatMap((s) => s.finance.events);
    // The season being raced counts too, as it does on the driver's card —
    // but only when it is a season past the settled one, never twice.
    const racing =
      live?.state.finance && live.seasonNumber > career.seasonNumber ? live.state.finance.events : [];
    return careerWinningsTable([...all, ...racing]).slice(0, 20);
  }, [career, history, live]);

  // The winter's views need a winter. Offering a tab that can only say "not yet"
  // is worse than not offering it.
  // D-EZ-102 — the newsstand is open from the first race; the trophy and the
  // Hall of Fame need a settled season.
  const views: View[] = career
    ? ['review', 'stats', 'rivals', 'money', 'garage', 'winter', 'story', 'news', 'hall', 'league']
    : ['stats', 'rivals', 'news', 'league'];
  const leagueAll = useMemo(() => leagueSeasons({ history, career, live }), [history, career, live]);
  const reader = usePaperReader({ history, career, live, leagueName });
  /** D-EZ-110 — the settled season's arc, for the story view (folded only when it is open). */
  const arc = useMemo(() => {
    if (view !== 'story' || !career) return null;
    try {
      return seasonArc(leagueAll.filter((s) => s.seasonNumber <= career.seasonNumber) as ChapterSeason[], career.seasonNumber);
    } catch {
      return null;
    }
  }, [view, career, leagueAll]);

  return (
    <>
      {career && (
        <header className="career__head">
          <div>
            <div className="career__kicker">
              Season {career.seasonNumber}
              <Cite> · seed {career.seed}</Cite>
            </div>
            <h2>{champion ? nameOf(champion) : 'No champion'}</h2>
            <p className="career__sub">
              {champion ? 'takes the Cup.' : 'The season did not produce one.'}{' '}
              {career.season.races.length} races ·{' '}
              {new Set(career.season.races.map((r) => r.race.winnerId)).size} different winners ·{' '}
              {career.offseason.demotions.length} lost their licence over the winter.
            </p>
          </div>
        </header>
      )}

      {/*
        D-EZ-73 (F7) — WHAT THE YEAR ACTUALLY DID, ABOVE THE TABS.

        From the design review, 20 Aug 2026:

          "Stats · Money · Garage · Winter · Story. Every one is worth reading
           and four of them are invisible until you go looking. The Winter in
           particular is where the drama lives — who lost a licence, who got
           promoted — and it's the fourth tab."

        Four tiles, and every one is a door: press it and you land on the tab
        that explains it. So the tabs are for reading in depth rather than for
        discovering that depth exists.

        ⚠ EVERY FIGURE IS THE SAME FOLD THE TAB BELOW IT USES. `shopBooks` is
        the money tab's own roll-up and `garageChange` is the garage tab's own
        reading — not a second arithmetic on the same question, which is how a
        summary and the page under it start disagreeing.
      */}
      {career && <CareerSummary career={career} onOpen={setView} />}

      {views.length > 1 && (
        <nav className="career__views" aria-label="Season views">
          {views.map((v) => (
            <button
              key={v}
              type="button"
              className={`seg${view === v ? ' is-on' : ''}`}
              onClick={() => setView(v)}
              aria-current={view === v ? 'true' : undefined}
            >
              {VIEW_LABEL[v]}
            </button>
          ))}
        </nav>
      )}

      {/* D-EZ-102 — the trophy, the newsstand and the Hall of Fame. */}
      {view === 'review' && career && (
        <ChampionsMoment
          seasons={leagueAll.filter((s) => s.seasonNumber <= career.seasonNumber)}
          seasonNumber={career.seasonNumber}
          onDriver={onDriver}
          onContinue={() => setView('winter')}
          onReadSeasonPaper={() => reader.open('season', career.seasonNumber)}
          onReadWinterPaper={() => reader.open('winter', career.seasonNumber)}
        />
      )}
      {view === 'review' && reader.modal}
      {view === 'news' && <Newsstand history={history} career={career} live={live} leagueName={leagueName} />}
      {view === 'hall' && (
        <HallOfFame
          seasons={leagueAll}
          throughSeason={leagueAll[leagueAll.length - 1]?.seasonNumber ?? career?.seasonNumber ?? 1}
          onDriver={onDriver}
        />
      )}
      {/* D-EZ-108 — the rivalry board: heat the dice put between drivers, remembered. */}
      {view === 'rivals' && <RivalryBoard seasons={leagueAll} onDriver={onDriver} />}
      {/* D-EZ-110 — eras, dynasties and the league season by season. */}
      {view === 'league' && <LeagueTimeline seasons={leagueAll as ChapterSeason[]} onDriver={onDriver} />}
      {view === 'stats' && (
        <StatsBoard
          sources={sources}
          leagueName={leagueName}
          onDriver={onDriver}
          onPlay={onPlayRaceByRace}
          liveThrough={liveThrough}
        />
      )}
      {view === 'money' && career && (
        <Money career={career} winnings={winnings} nameOf={nameOf} onDriver={onDriver} />
      )}
      {view === 'garage' && career && <Garage career={career} onDriver={onDriver} />}
      {/*
        D-EZ-73 (F9) — THE WINTER AND THE STORY ARE THINGS YOU READ.

        From the design review, 20 Aug 2026:

          "The game has a dark instrument panel and a light printed document…
           anything you operate is dark; anything you read is paper. Then the
           winter report and the season story — currently dark — should be
           paper, because you read them."

        ⚠ ONE CLASS FLIPS THE WHOLE SUBTREE AND NOT ONE CHILD KNOWS. `.readable`
        redefines the colour tokens on the container, and custom properties
        inherit — so every tile, rule, list and label underneath resolves to
        newsprint without a single component being edited. That is the argument
        for F1's token layer, made here in one rule.
      */}
      {view === 'winter' && career && (
        <>
          {/* D-EZ-110 — the winter told as chapters, then the working underneath. */}
          <WinterChapters seasons={leagueAll as ChapterSeason[]} seasonNumber={career.seasonNumber} onDriver={onDriver} />
          <div className="readable">
            <h3 className="chap__workhead">The winter’s working</h3>
            <Winter career={career} onDriver={onDriver} />
          </div>
        </>
      )}
      {view === 'story' && career && (
        <div className="readable">
          <SeasonStory
            events={fromSeasonEvents(career.season.events)}
            seasonNumber={career.seasonNumber}
            nameOf={new Map(career.roster.map((c) => [c.driverId, c.name]))}
            raceNames={new Map(career.season.races.map((r) => [r.raceNumber, r.scheduled.raceName]))}
            arc={arc}
          />
        </div>
      )}
    </>
  );
}

/**
 * D-EZ-73 (F7) — THE FOUR THINGS THAT HAPPENED, EACH A DOOR.
 *
 * ⚠ A TILE THAT CANNOT BE ANSWERED SAYS SO AND STAYS. A first season has no
 * winter behind it, and a tile that vanished would make the row a different
 * shape every year — which is worse than one that reads "not yet", because a
 * player learns where things are by their position.
 */
function CareerSummary({
  career,
  onOpen,
}: {
  career: CareerSeason;
  onOpen: (view: View) => void;
}) {
  const summary = useMemo(() => {
    const champion = career.season.championId
      ? (career.season.finalStandings.byDriver[career.season.championId]?.name ?? null)
      : null;
    const richest = shopBooks(career)[0] ?? null;
    const garage = garageChange(career);
    const lost = career.offseason.demotions;
    return { champion, richest, garage, lost };
  }, [career]);

  const tiles: { key: View; label: string; value: string; note: string }[] = [
    {
      key: 'stats',
      label: 'Champion',
      value: summary.champion ?? 'None',
      note: summary.champion ? `Season ${career.seasonNumber}` : 'The season did not produce one',
    },
    {
      key: 'money',
      label: 'Richest shop',
      value: summary.richest ? summary.richest.shop : '—',
      note: summary.richest ? `${formatDollars(summary.richest.balanceMb)} on the year` : 'No books yet',
    },
    {
      key: 'garage',
      label: 'Seats changed hands',
      value: String(summary.garage.counts.refilled),
      note:
        summary.garage.counts.refilled === 0
          ? 'Every car stayed where it was'
          : `${summary.garage.counts.rookiesIntoCharters} to rookies · ${summary.garage.counts.independentsAfter} still without a charter`,
    },
    {
      key: 'winter',
      label: 'Lost their licence',
      value: String(summary.lost.length),
      note:
        summary.lost.length === 0
          ? 'Nobody went home'
          : `${summary.lost[0].name}${summary.lost.length > 1 ? ` and ${summary.lost.length - 1} more` : ''}`,
    },
  ];

  return (
    <div className="csum" aria-label="The season at a glance">
      {tiles.map((t) => (
        <button key={t.key} type="button" className="csum__tile" onClick={() => onOpen(t.key)}>
          <span className="csum__l">{t.label}</span>
          <span className="csum__v">{t.value}</span>
          <span className="csum__n">{t.note}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * ⚠ EXPORTED FOR THE LOOK, NOT FOR A CALLER. `CareerScreen` is the only thing
 * that renders this; the export exists so the money tab can be rendered on its
 * own and screenshotted, which is how the two-level books were checked. The
 * same reason `NextGarage` and `whatIsAtRisk` are exported.
 */
export function Money({
  career,
  winnings,
  nameOf,
  onDriver,
}: {
  career: CareerSeason;
  winnings: { driverId: string; winningsMb: number }[];
  nameOf: (id: string) => string;
  onDriver: (id: string) => void;
}) {
  const top = winnings[0]?.winningsMb ?? 1;
  const shops = useMemo(() => shopBooks(career), [career]);
  const cars = useMemo(() => carBooks(career), [career]);
  const free = useMemo(() => privateers(career), [career]);
  return (
    <div className="cmoney">
      <section>
        <h3>Career winnings</h3>
        <div className="bars">
          {winnings.map((w) => (
            <div className="bar bar--money" key={w.driverId}>
              <button type="button" className="bar__t linkish" onClick={() => onDriver(w.driverId)}>
                {nameOf(w.driverId)}
              </button>
              <div className="bar__track">
                <div className="bar__fill" style={{ width: `${(w.winningsMb / top) * 100}%` }} />
              </div>
              <div className="bar__v">{formatDollars(w.winningsMb)}</div>
            </div>
          ))}
        </div>
        <p className="dim small">Purse and contingency only — money won on the racetrack, not money paid to show up.</p>
      </section>

      {/*
        D-EZ-72 — THE SHOPS, WHICH IS THE LEVEL THE CHARTER RULE ACTUALLY WORKS AT.

        Howard: *"the P&L on the right shows their driver but shouldnt it be the
        name of the team?"* The car rows are right — in this game's money rules
        the car IS the team (SAG/12) — but "the garage's books" was written over
        them and the charter rule was printed underneath, and both of those are
        about the SHOP. Neither level said which it was and the shop level was
        not on the page at all.
      */}
      <section>
        <h3>The shops</h3>
        <table className="ctable">
          <thead>
            <tr>
              <th>Shop</th>
              <th className="num">Income</th>
              <th className="num">Out</th>
              <th className="num">P&amp;L</th>
            </tr>
          </thead>
          <tbody>
            {shops.map((s) => (
              <tr key={s.teamId}>
                <td className="dim" title={s.cars.join(', ')}>
                  {s.shop}
                  {s.carsInTheRed > 0 && (
                    <span className="dim shopred">
                      {' '}· {s.carsInTheRed}/{s.cars.length} red
                    </span>
                  )}
                </td>
                <td className="num">{formatDollars(s.incomeMb)}</td>
                <td className="num">{formatDollars(-s.expenseMb)}</td>
                <td className={`num ${s.balanceMb >= 0 ? 'mny' : 'bad'}`}>{formatDollars(s.balanceMb)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="dim small">
          {/*
            ⚠ THE RULE, STATED WHERE IT APPLIES AND WITH ITS TWO CONDITIONS ON IT.
            It used to sit under the CAR table saying "a team", which invited a
            reader to count fifty-three cars in the red as fifty-three charters in
            danger. It folds a SHOP, on the sum of its three cars, CUMULATIVELY —
            not on one bad year.
          */}
          A shop's position is its cars added up, and a charter is only sold when that runs more than a season&apos;s
          operating cost under water — cumulatively, across the career, not from one bad year<Cite> (D-031)</Cite>. This table is
          this season alone.
        </p>
      </section>

      <section className="cmoney__wide">
        <h3>The books, car by car</h3>
        <p className="dim small">
          {/*
            ⚠ WHY THE ROWS ARE DRIVERS, said on the screen instead of only in the
            source. The guide is explicit — "A Team can have only one Driver" —
            and the consequence is the interesting part: a shop cannot bail out
            its third car with the first car's winnings.
          */}
          One car is one team in the money rules<Cite> (SAG/12)</Cite>, so every car keeps its own books — a shop cannot move money
          from one of its cars to another.
        </p>
        <table className="ctable">
          <thead>
            <tr>
              <th>Car</th>
              <th>Shop</th>
              <th className="num">Income</th>
              <th className="num">Out</th>
              <th className="num">P&amp;L</th>
            </tr>
          </thead>
          <tbody>
            {cars.slice(0, CAR_ROWS).map((c) => (
              <tr key={c.driverId}>
                <td>
                  <button type="button" className="linkish" onClick={() => onDriver(c.driverId)}>
                    {c.driver}
                  </button>
                </td>
                <td className="dim">{c.shop ?? 'no charter'}</td>
                <td className="num">{formatDollars(c.incomeMb)}</td>
                <td className="num">{formatDollars(-c.expenseMb)}</td>
                <td className={`num ${c.balanceMb >= 0 ? 'mny' : 'bad'}`}>{formatDollars(c.balanceMb)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="dim small">
          {/*
            ⚠ AND WHAT IS NOT ON THE TABLE IS COUNTED. This was `slice(0, 16)`
            with nothing said about the other thirty-seven.
          */}
          {cars.length > CAR_ROWS
            ? `The top ${CAR_ROWS} of ${cars.length}. `
            : ''}
          {career.report.insolventTeams} of {career.report.teams.length} cars finished the year in the red
          {free > 0 ? `, and ${free} of them run without a charter` : ''}.
        </p>
      </section>
    </div>
  );
}

/**
 * How many car rows the money tab prints. A judgement, not a limit: fifty-three
 * rows of five columns is a spreadsheet, and the shops table above already
 * accounts for every one of them. Whatever is left off is COUNTED underneath.
 */
const CAR_ROWS = 20;

function Garage({ career, onDriver }: { career: CareerSeason; onDriver: (id: string) => void }) {
  return (
    <div className="cgarage">
      {career.teams.map((team) => {
        const grade = career.offseason.crewGrades[team.teamId];
        const drivers = team.driverIds
          .map((id) => career.season.finalStandings.byDriver[id])
          .filter(Boolean)
          .sort((a, b) => a.rank - b.rank);
        return (
          <article className="shop" key={team.teamId} style={{ borderTopColor: team.livery?.base ?? undefined }}>
            <header>
              <h4>{team.name}</h4>
              <span className="shop__grade">crew {grade ?? '—'}</span>
            </header>
            <ul>
              {drivers.map((d) => (
                <li key={d.driverId}>
                  <button type="button" className="linkish" onClick={() => onDriver(d.driverId)}>
                    {d.name}
                  </button>
                  <span className="dim">
                    {ordinal(d.rank)} · {d.wins} win{d.wins === 1 ? '' : 's'}
                  </span>
                </li>
              ))}
            </ul>
          </article>
        );
      })}
    </div>
  );
}

function Winter({ career, onDriver }: { career: CareerSeason; onDriver: (id: string) => void }) {
  const ct = useCiteText();
  const { offseason } = career;
  const moved = offseason.rerates.filter((r) => r.steps.some((s) => s.changed));
  return (
    <div className="cwinter">
      <div className="cwinter__stats">
        <Tile v={offseason.unranked.length} l="Below the 75% line" n={ct('SAG/5', 'not ranked this year')} />
        <Tile v={offseason.demotions.length} l="Lost their licence" n={ct('worst first, capped at 13 (D-032)')} hot />
        <Tile v={offseason.rookies.length} l="Rookies signed" n="on the inaugural tables" />
        <Tile v={offseason.teamFailures.length} l="Teams folded" n={ct('charter sold and re-branded (D-031)')} />
      </div>

      <section>
        <h3>Who left</h3>
        {offseason.demotions.length === 0 ? (
          <p className="dim">Nobody. Unusual.</p>
        ) : (
          <ul className="dlist">
            {offseason.demotions.map((d) => (
              <li key={d.driverId}>
                <button type="button" className="linkish" onClick={() => onDriver(d.driverId)}>
                  {d.name}
                </button>
                <span className="dim"> — {theSpecificPart(d.name, d.description)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- what the winter left the garage looking like (D-EZ-62) ---- */}
      <NextGarage career={career} onDriver={onDriver} />

      <section>
        <h3>Cards that moved</h3>
        <ul className="dlist dlist--story">
          {moved.slice(0, 20).map((r) => (
            <li key={r.driverId}>
              <button type="button" className="linkish" onClick={() => onDriver(r.driverId)}>
                {r.name}
              </button>
              <span className="dim"> — {r.story.replace(`${r.name} `, '')}</span>
            </li>
          ))}
        </ul>
        {moved.length > 20 && <p className="dim small">…and {moved.length - 20} more. Click any name for the full working.</p>}
      </section>
    </div>
  );
}

/**
 * THE GARAGE THE WINTER PRODUCED — D-EZ-62.
 *
 * Howard, 19 Aug 2026: *"It would seem we lost some garage's/teams but the game
 * has to make new garage's and teams to compete and have spots for rookies as
 * well in addition to independents no?"*
 *
 * It does all of that, and it always did. What it never did was SAY so: this
 * view counted who lost a licence and how many rookies were signed and then
 * stopped, so thirteen names disappearing looked exactly like a league emptying
 * out. Showing the losses without the replacements is not neutral.
 *
 * So the sentence comes first and answers the question in words — is the field
 * still whole — and the shops underneath show every seat and where the man in
 * it came from.
 */
export function NextGarage({ career, onDriver }: { career: CareerSeason; onDriver: (id: string) => void }) {
  const change = useMemo(() => garageChange(career), [career]);
  const { counts } = change;
  const whole = counts.shopsAfter === counts.shopsBefore && counts.seatsAfter === counts.seatsBefore;

  return (
    <section>
      <h3>The garage next season</h3>
      <p className={`ngar__line${whole ? '' : ' ngar__line--changed'}`}>{garageLine(change)}</p>

      {change.closed.length > 0 && (
        <ul className="dlist">
          {change.closed.map((c) => (
            <li key={c.teamId} className="warn">{c.story}</li>
          ))}
        </ul>
      )}

      <div className="cgarage">
        {change.shops.map((shop) => (
          <article className="shop" key={shop.teamId}>
            <header>
              <h4>{shop.name}</h4>
              <span className="shop__grade">crew {shop.crew ?? '—'}</span>
            </header>
            {shop.wasCalled && (
              /* The charter survived and was bought — the same cars under a new
                 name. Saying only the new name would read as a team appearing
                 out of nowhere beside one that vanished. */
              <p className="ngar__sold">
                bought — ran last season as <b>{shop.wasCalled}</b>
              </p>
            )}
            <ul>
              {shop.seats.map((seat) => (
                <li key={seat.driverId}>
                  <button type="button" className="linkish" onClick={() => onDriver(seat.driverId)}>
                    {seat.name}
                  </button>
                  {/*
                    ⚠ SHORT, WITH THE LONG FORM ON HOVER — a screenshot caught
                    "up from independent" wrapping to two lines and bursting out
                    of its own border inside a narrow shop card. A tag that
                    breaks the card it sits in is worse than no tag.
                  */}
                  <span
                    className={`ngar__tag ngar__tag--${seat.origin}`}
                    title={
                      seat.origin === 'stayed'
                        ? 'In this shop last season and still in it'
                        : seat.origin === 'rookie'
                          ? 'Signed this winter, on the inaugural tables'
                          : seat.origin === 'independent'
                            ? 'Ran his own car last season; picked up for a charter seat'
                            : `Moved across from ${seat.fromTeam}`
                    }
                  >
                    {seat.origin === 'stayed'
                      ? 'stays'
                      : seat.origin === 'rookie'
                        ? 'rookie'
                        : seat.origin === 'independent'
                          ? 'moved up'
                          : 'switched'}
                  </span>
                </li>
              ))}
            </ul>
            {shop.gone.length > 0 && (
              <ul className="ngar__gone">
                {shop.gone.map((left) => (
                  <li key={left.driverId}>
                    <button type="button" className="linkish" onClick={() => onDriver(left.driverId)}>
                      {left.name}
                    </button>
                    <span className="dim">
                      {' '}— {left.reason === 'licence'
                        ? 'lost his licence'
                        : left.reason === 'retired'
                          ? 'retired'
                          : left.reason === 'died'
                            ? 'killed during the season'
                            : `to ${left.toTeam ?? 'his own car'}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </div>

      {/*
        THE PRIVATEERS, NAMED. They are not "the leftovers" — a driver without a
        charter is running his own car, which is a thing the rulebook has always
        had and this screen has never shown. On Howard's own league every one of
        them next season is a rookie, which is the whole story of the winter in
        one list and was completely invisible.
      */}
      <h4 className="ngar__indhead">
        Running without a charter — {counts.independentsAfter}
        {change.independents.some((i) => i.rookie) && (
          <span className="dim">
            {' '}· {change.independents.filter((i) => i.rookie).length} of them rookies
          </span>
        )}
      </h4>
      {change.independents.length === 0 ? (
        <p className="dim">Nobody. Every licensed driver has a charter seat.</p>
      ) : (
        <ul className="ngar__ind">
          {change.independents.map((p) => (
            <li key={p.driverId}>
              <button type="button" className="linkish" onClick={() => onDriver(p.driverId)}>
                {p.name}
              </button>
              {p.rookie && <span className="ngar__tag ngar__tag--rookie">rookie</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Tile({ v, l, n, hot }: { v: number | string; l: string; n: string; hot?: boolean }) {
  return (
    <div className="tile">
      <div className={`tile__v${hot ? ' is-hot' : ''}`}>{v}</div>
      <div className="tile__l">{l}</div>
      <div className="tile__n">{n}</div>
    </div>
  );
}

/**
 * A demotion's full sentence ends with the same clause every time — "He does
 * not qualify for a ranking and loses his licence before anyone is demoted on
 * points." Correct once, and thirteen of them in a column is exactly the
 * repetition Howard flagged as the thing that breaks the realism. The list
 * keeps the part that differs; the dossier still shows the whole sentence.
 */
function theSpecificPart(name: string, description: string): string {
  const withoutName = description.replace(`${name} `, '');
  const firstSentence = withoutName.split(/\.\s+/)[0];
  return firstSentence.endsWith('.') ? firstSentence : `${firstSentence}.`;
}

// ---------------------------------------------------------------------------
// What a press of the erasing button would cost — D-EZ-57
// ---------------------------------------------------------------------------

export interface AtRisk {
  /** Seasons on this page that the press would throw away. */
  seasons: number;
  /** A season is part-raced on the Set up tab. */
  live: boolean;
  liveNumber: number | null;
  /** 1-based number of the NEXT race, so races run is one less. */
  liveRace: number | null;
  saved: boolean;
  /** Is there anything to lose at all? Nothing to lose, no confirmation. */
  any: boolean;
}

/**
 * ⚠ PURE, AND EXPORTED, SO THE SAFETY CAN BE TESTED WITHOUT A BROWSER.
 *
 * The whole guard turns on `any`: false means the press runs immediately, true
 * means it only arms. Getting that backwards in either direction is a bug with
 * a cost — a needless confirmation on an empty page, or no confirmation on a
 * three-season league.
 *
 * The history counts even when the workbench is empty. `onSeason(next, false)`
 * sets it to `[]` whether or not there is a current season, so seasons behind
 * the one on screen are just as gone.
 */
export function whatIsAtRisk(input: {
  career: CareerSeason | null;
  history: readonly CareerSeason[];
  live?: LiveSeasonLike | null;
  seasonInPlay?: { seasonNumber: number; state: { nextRace: number } } | null;
  saved: boolean;
}): AtRisk {
  const seasons = (input.career ? 1 : 0) + input.history.length;
  const live = Boolean(input.live);
  return {
    seasons,
    live,
    liveNumber: input.seasonInPlay?.seasonNumber ?? null,
    liveRace: input.seasonInPlay?.state.nextRace ?? null,
    saved: input.saved,
    any: seasons > 0 || live,
  };
}
